/* @jsxImportSource @emotion/react */
import React from 'react'
import classNames from 'classnames'
import { useIntl } from 'react-intl'

import { CustomNoteIcon } from '@slite/components'
import type { Icon } from '@slite/icons'
import { CaretNavigationRightIcon, HomeIcon, SearchIcon } from '@slite/icons'

import type {
  HelpNoteSummary,
  HelpNoteTreeNode,
} from '../../data/helpCenterFormat'
import { helpArticlePathParams } from '../../data/helpCenterFormat'
import { useRoutes } from '../../routes'
import { HelpSearchTrigger } from './HelpSearchTrigger'
import { useHelpSearch } from './useHelpSearch'

// Nested rows indent one icon-width per level (8px row padding + 16px step).
const rowIndent = (depth: number) => 8 + depth * 16

function HelpSidebarRow({
  title,
  href,
  depth,
  icon,
  isActive,
  isExpanded,
  onToggleExpand,
  dataTestId,
}: {
  title: string
  href: string
  depth: number
  icon: React.ReactNode
  isActive: boolean
  isExpanded?: boolean
  onToggleExpand?: () => void
  dataTestId?: string
}) {
  return (
    <a
      href={href}
      aria-current={isActive ? 'page' : undefined}
      data-test-id={dataTestId}
      className="flexbox flexbox-align-center flexbox-gap-8"
      css={{
        minHeight: 30,
        padding: '5px 6px',
        paddingLeft: rowIndent(depth),
        marginBottom: 1,
        borderRadius: 8,
        fontSize: 'var(--font-size-7)',
        lineHeight: 1.5,
        textDecoration: 'none',
        color: isActive ? 'var(--shade-00)' : 'var(--shade-05)',
        fontWeight: isActive ? 600 : 400,
        background: isActive ? 'var(--moon-dust)' : 'transparent',
        transition: 'background 0.15s ease, color 0.15s ease',
        ':hover': { background: 'var(--moon-dust)', color: 'var(--shade-00)' },
        ':hover .help-sidebar-caret, :focus-within .help-sidebar-caret': {
          opacity: 1,
        },
      }}
    >
      <span
        className="flexbox flexbox-centered flexbox-no-shrink"
        css={{ width: 18, height: 18 }}
      >
        {icon}
      </span>
      <span className="ellipsis flexbox-grow">{title}</span>
      {onToggleExpand && (
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-label={title}
          className="help-sidebar-caret flexbox flexbox-centered flexbox-no-shrink border-none background-transparent cursor-pointer padding-0 shade-30"
          css={{
            width: 20,
            height: 20,
            borderRadius: 6,
            opacity: 0,
            transition: 'opacity 0.15s ease',
            ':hover': {
              background: 'var(--moon-dust-darker)',
              color: 'var(--shade-05)',
            },
          }}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            onToggleExpand()
          }}
        >
          <CaretNavigationRightIcon
            size={12}
            className={classNames('transition', { 'rotate-90': isExpanded })}
          />
        </button>
      )}
    </a>
  )
}

function ShortcutRow({
  title,
  href,
  icon: IconComponent,
  isActive,
  dataTestId,
}: {
  title: string
  href: string
  icon: Icon
  isActive: boolean
  dataTestId?: string
}) {
  return (
    <HelpSidebarRow
      title={title}
      href={href}
      depth={0}
      icon={<IconComponent size={16} />}
      isActive={isActive}
      dataTestId={dataTestId}
    />
  )
}

export function HelpSidebar({
  root,
  categories,
  currentNoteId,
  expandedIds,
}: {
  root: HelpNoteSummary
  categories: HelpNoteTreeNode[]
  currentNoteId: string
  expandedIds: string[]
}) {
  const intl = useIntl()
  const routes = useRoutes()
  const openSearch = useHelpSearch()
  const [expanded, setExpanded] = React.useState(() => new Set(expandedIds))

  const articleHref = (item: { id: string; title: string }) =>
    routes.helpCenter.article(helpArticlePathParams(item))

  const toggleExpand = (noteId: string) =>
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(noteId)) {
        next.delete(noteId)
      } else {
        next.add(noteId)
      }
      return next
    })

  // The whole tree is loaded up front, so expansion is pure local state.
  const renderNodes = (nodes: HelpNoteTreeNode[], depth: number) =>
    nodes.map((node) => (
      <React.Fragment key={node.id}>
        <HelpSidebarRow
          title={node.title}
          href={articleHref(node)}
          depth={depth}
          icon={
            <CustomNoteIcon
              iconShape={node.iconShape}
              iconColor={node.iconColor}
              size={16}
            />
          }
          isActive={node.id === currentNoteId}
          isExpanded={expanded.has(node.id)}
          onToggleExpand={
            node.children.length > 0 ? () => toggleExpand(node.id) : undefined
          }
          dataTestId="helpSidebarItem"
        />
        {node.children.length > 0 &&
          expanded.has(node.id) &&
          renderNodes(node.children, depth + 1)}
      </React.Fragment>
    ))

  return (
    <nav className="flexbox flexbox-column" data-test-id="helpSidebar">
      {/* Pinned to the top of the scrolling column so search stays reachable. */}
      <div
        css={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'var(--moon-dust-lighter)',
          padding: '17px 0 12px',
        }}
      >
        <button
          type="button"
          data-test-id="helpSidebarSearch"
          onClick={openSearch}
          className="flexbox flexbox-align-center flexbox-gap-8 background-white cursor-pointer width-full"
          css={{
            height: 36,
            padding: '0 10px',
            borderRadius: 10,
            border: '1px solid var(--shade-40)',
            color: 'var(--shade-20)',
            fontSize: 'var(--font-size-7)',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            ':hover': {
              borderColor: 'var(--shade-30)',
              boxShadow: '0 1px 4px rgba(38, 42, 48, 0.06)',
            },
          }}
        >
          <SearchIcon size={15} className="flexbox-no-shrink" />
          <span className="flexbox-grow text-align-left">
            {intl.formatMessage({
              id: 'website.helpCenter.sidebar.search',
              defaultMessage: 'Search',
            })}
          </span>
          <HelpSearchTrigger />
        </button>
      </div>

      <ShortcutRow
        title={intl.formatMessage({
          id: 'website.helpCenter.sidebar.introduction',
          defaultMessage: 'Introduction',
        })}
        href={routes.helpCenter.index()}
        icon={HomeIcon}
        isActive={currentNoteId === root.id}
        dataTestId="helpSidebarRoot"
      />

      <div
        className="uppercase font-weight-bold shade-30 font-size-8"
        css={{ letterSpacing: '0.5px', margin: '24px 8px 8px' }}
      >
        {intl.formatMessage({
          id: 'website.helpCenter.sidebar.guides',
          defaultMessage: 'Guides',
        })}
      </div>
      {renderNodes(categories, 0)}
    </nav>
  )
}
